import { Box, Pagination as PaginationComponent, TextField } from '@mui/material';
import * as React from 'react';
import { useState } from 'react';
import { appConstants as c } from '../app';
import { useDebounce } from '../hooks';
import { FetchLedgerEntriesPayload, Pagination } from '../types';
import { validateDate } from '../utils';

interface Props {
  filterLedgerEntriesHandler: (payload: FetchLedgerEntriesPayload) => void;
  pagination?: Pagination;
}

export const ToolbarComponent: React.FC<Props> = ({ filterLedgerEntriesHandler, pagination }) => {
  const [projectName, setProjectName] = useState<string>('');
  const [issueCode, setIssueCode] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [currentPage, setCurrentPage] = React.useState<number>(pagination && pagination.currentPage ? pagination.currentPage : 1);
  const [dateStartValid, setDateStartValid] = useState<boolean>(true);
  const [dateEndValid, setDateEndValid] = useState<boolean>(true);
  // TODO: cleanUp
  // const [numOfPages, setNumOfPages] = React.useState<number>(pagination && pagination.numOfPages ? pagination.numOfPages : 1);

  // the trick for solve the `React Input component it always comes one step behind`
  // is using a useEffect hook to call applyFilterCondition when conditions change in debounce
  React.useEffect(() => {
    applyFilterCondition();
    // TODO: cleanUp
    // setCurrentPage(pagination && pagination.currentPage ? pagination.currentPage : 1)
    // setNumOfPages(pagination && pagination.numOfPages ? pagination.numOfPages : 1)
    // log(`ToolbarComponent pagination ${JSON.stringify(pagination, undefined, 2)}\ncurrentPage: ${pagination && pagination.currentPage ? pagination.currentPage : 1}/${pagination && pagination.currentPage ? pagination.currentPage : 1}`);
  }, [projectName, issueCode, description, userName, startDate, endDate, currentPage]);

  const applyFilterCondition = () => {
    const filterArray = [];
    if (projectName) {
      filterArray.push(`LOWER(ledger_entries.project_name) LIKE LOWER('%${projectName}%')`)
    }
    if (issueCode) {
      filterArray.push(`LOWER(ledger_entries.issue_code) LIKE LOWER('%${issueCode}%')`)
    }
    if (userName) {
      filterArray.push(`LOWER(ledger_entries.user_name) LIKE LOWER('%${userName}%')`)
    }
    if (description) {
      filterArray.push(`LOWER(ledger_entries.description) LIKE LOWER('%${description}%')`)
    }
    if (startDate) {
      filterArray.push(`ledger_entries.created >= '${startDate} 00:00:00'`)
    }
    if (endDate) {
      filterArray.push(`ledger_entries.created <= '${endDate} 23:59:59'`)
    }

    const filter = filterArray.join(' AND ');
    const filterCondition = filter && filter != '' ? filter : undefined;
    const sortCondition = c.VALUES.ledger.defaultOrder;
    const payload = {
      filter: {
        condition: filterCondition,
        limit: c.VALUES.ledger.defaultLimit,
        offset: (currentPage - 1) * c.VALUES.ledger.defaultLimit,
      },
      sort: {
        condition: sortCondition
      }
    };
    // log(`applyFilterCondition payload: ${JSON.stringify(payload, undefined, 2)}`);
    filterLedgerEntriesHandler(payload);
  }

  const projectNameInputHandler = useDebounce<React.ChangeEvent<HTMLInputElement>>(
    (e) => setProjectName(e.target.value),
    c.VALUES.debounceValue
  );
  const issueCodeInputHandler = useDebounce<React.ChangeEvent<HTMLInputElement>>(
    (e) => setIssueCode(e.target.value),
    c.VALUES.debounceValue
  );
  const descriptionInputHandler = useDebounce<React.ChangeEvent<HTMLInputElement>>(
    (e) => { setDescription(e.target.value); applyFilterCondition(); },
    c.VALUES.debounceValue
  );
  const userNameInputHandler = useDebounce<React.ChangeEvent<HTMLInputElement>>(
    (e) => { setUserName(e.target.value); applyFilterCondition(); },
    c.VALUES.debounceValue
  );
  const startDateInputHandler = useDebounce<React.ChangeEvent<HTMLInputElement>>(
    (e) => {
      const valid = validateDate(e.target.value);
      setDateStartValid(valid || !e.target.value);
      if (valid) {
        setStartDate(e.target.value); applyFilterCondition();
      }
    },
    c.VALUES.debounceValue
  );
  const endDateInputHandler = useDebounce<React.ChangeEvent<HTMLInputElement>>(
    (e) => {
      const valid = validateDate(e.target.value);
      setDateEndValid(valid || !e.target.value);
      if (valid) {
        setEndDate(e.target.value); applyFilterCondition();
      }
    },
    c.VALUES.debounceValue
  );

  const pagingChangeHandler = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  return (
    <Box
      component="form"
      sx={{
        '& .MuiTextField-root': { my: 1, marginRight: 1, width: '24ch' },
      }}
      noValidate
      autoComplete="off"
    >
      <div>
        <TextField
          id='projectName'
          label='Project Name'
          placeholder='c3-system-tailor'
          defaultValue={projectName}
          onChange={projectNameInputHandler}
        // helperText={projectName}
        />
        <TextField
          id='issueCode'
          label='Issue Code'
          defaultValue={issueCode}
          onChange={issueCodeInputHandler}
          // helperText={issueCode}
        />
        <TextField
          id='description'
          label='Description'
          defaultValue={description}
          onChange={descriptionInputHandler}
          // helperText={description}
        />
        <TextField
          id='userName'
          label='Username'
          defaultValue={userName}
          onChange={userNameInputHandler}
          // helperText={userName}
        />
        <TextField
          id='startDate'
          label='Start Date'
          placeholder='2022-01-01'
          defaultValue={startDate}
          onChange={startDateInputHandler}
          error={!dateStartValid}
          // helperText={startDate}
        />
        <TextField
          id='endDate'
          label='End Date'
          placeholder='2022-01-31'
          defaultValue={endDate}
          onChange={endDateInputHandler}
          error={!dateEndValid}
          // helperText={endDate}
        />
        <Box
          component="div"
          sx={{
            '& .MuiPagination-root': { my: 2, align: 'right' },
          }}
        >
          <PaginationComponent page={currentPage} defaultPage={1} count={pagination && pagination.numOfPages ? pagination.numOfPages : 1} siblingCount={0} boundaryCount={2} onChange={pagingChangeHandler} />
        </Box>
      </div>
    </Box>
  );
};
